import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus } from "lucide-react";
import * as z from "zod";
import { addClientClientsPost } from "@/client-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";

const createClientSchema = z.object({
  client_code: z.string().min(1, "Client code is required").max(10, "Client code must be at most 10 characters"),
  client_name: z.string().min(1, "Client name is required").max(255, "Client name must be at most 255 characters"),
});

type CreateClientFormValues = z.infer<typeof createClientSchema>;

export function CreateClientDialog() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<CreateClientFormValues>({
    resolver: zodResolver(createClientSchema),
    defaultValues: {
      client_code: "",
      client_name: "",
    },
  });

  const { mutateAsync: createClient, isPending } = useMutation({
    mutationFn: addClientClientsPost,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast({
        title: t("Success"),
        description: t("Client created successfully"),
      });
      setOpen(false);
      form.reset();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      let errorMessage = "Failed to create client. Please try again later.";

      if (error && typeof error === "object" && "body" in error && "detail" in error.body) {
        errorMessage = error.body.detail;
      }

      toast({
        title: t("Error"),
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (data: CreateClientFormValues) => {
    await createClient({
      requestBody: data,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="flex gap-2">
          <Plus className="h-4 w-4" />
          {t("Create Client")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("Create New Client")}</DialogTitle>
          <DialogDescription>
            {t("Enter the details for the new client. Both client code and name are required.")}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="client_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Client Code")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("Enter client code (max 10 characters)")} {...field} maxLength={10} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="client_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Client Name")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("Enter client name")} {...field} maxLength={255} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  form.reset();
                }}
                disabled={isPending}
              >
                {t("Cancel")}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? t("Creating...") : t("Create Client")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
